import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  ListItem,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Close, Settings } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import LoopIcon from '@mui/icons-material/Loop';
import { setHistoryData } from 'store/Document';
import { useBoundStore } from 'store';
import { isEmpty } from 'lodash';
import { LocalizationContext } from 'contexts';
import { stripHtml } from 'helpers/html';
import { markInvalidAnchors } from 'helpers/string';
import { NUMBERING_DOT, renderArticleComparisonNumbering, renderBlockComparisonNumbering } from 'helpers/numbering';
import { transformComparisonToHistory } from 'clients/documents/documentsClient.formatter';
import {
  ChangeType,
  ComparisonChange,
  Numbering,
  NumberingItem,
  GroupParent,
} from 'clients/documents/documentsClient.types';
import { documentsClient } from 'clients/documents/documentsClient';
import { handleExportClick } from './exportUtils';
import VersionSettingsDialog from './VersionSettingsDialog';

export interface CLAVersion {
	slug: string;
	title: string;
	status: string;
	published_at: string | null;
	created_at: string;
	updated_at: string;
	is_current: boolean;
}

export interface VersionHistoryResponse {
	version_group_id: number;
	current_version: string;
	total_versions: number;
	versions: CLAVersion[];
}

export interface CompareVersionsResponse {
	source_cla: string;
	target_cla: string;
	source_slug: string;
	target_slug: string;
	source_draft: boolean;
	target_draft: boolean;
	total_changes: number;
	changes: ComparisonChange[];
}

export interface FilterState {
	showRenvoiOnly: boolean;
	showImportantOnly: boolean;
	hideRenvoi: boolean;
	hideImportant: boolean;
}

export interface VersionComparisonPanelProps {
	show: boolean;
	onClose: () => void;
	currentClaSlug: string;
	onVersionsSelected?: (sourceSlug: string, targetSlug: string, sourceIsDraft: boolean, targetIsDraft: boolean) => void;
	onDocumentTitleChange?: (sourceTitle: string, targetTitle: string) => void;
	onComparisonDataChange?: (changesCount: number) => void;
}

interface TargetOption {
	slug: string;
	isDraft: boolean;
	title: string;
	published_at?: string | null;
	created_at?: string;
	is_current?: boolean;
	isCurrentDraft?: boolean;
}

const getGroupingParents = (change: any): GroupParent[] => {
  const parents = change?.grouping_parents;
  return Array.isArray(parents) ? parents : [];
};

const headersForTransition = (prev: GroupParent[], curr: GroupParent[]): GroupParent[] => {
  let i = 0;
  while (
    i < prev.length &&
			i < curr.length &&
			prev[i]?.id === curr[i]?.id &&
			prev[i]?.entity_type === curr[i]?.entity_type
  ) {
    i++;
  }
  return curr.slice(i);
};

const headerSxForLevel = (level: number) => ({
  px: 1.5,
  py: 0.75,
  marginLeft: level === 0 ? 0 : '3px',
  marginTop: level === 0 ? 1.5 : 1,
  color: '#4C4E4E',
});

const headerTextSxForLevel = (level: number) => ({
  fontWeight: 600,
  fontSize: level === 0 ? '1rem' : '0.8rem',
  color: 'gray',
  lineHeight: 1.25,
});


const ADDED_FOOTNOTE_COLOR = '#3da447';
const REMOVED_FOOTNOTE_COLOR = '#bc262c';
const DEFAULT_TO_COLOR = '#424242';
const DEFAULT_FROM_COLOR = '#9e9e9e';

const versionCache = new Map<string, VersionHistoryResponse>();
const comparisonCache = new Map<string, CompareVersionsResponse>();
const comparisonLastDoneAt = new Map<string, number>();
let currentlyHighlightedElements: HTMLElement[] = [];

const SOURCE_CONTAINER_ID = 'document-source';
const TARGET_CONTAINER_ID = 'document-target';

type RenderNumberingProps = {
	article_numbering_data: NumberingItem[] | null | [],
	block_numbering_data: NumberingItem[] | null | []
}

export const RenderNumbering: FC<RenderNumberingProps> = ({ article_numbering_data, block_numbering_data }) => {
  if (isEmpty(article_numbering_data)) {
    return (
      <>
        {renderArticleComparisonNumbering(block_numbering_data?.slice(1, 3))}
        {renderArticleComparisonNumbering(block_numbering_data?.slice(3))}
      </>
    );
  }
  return (
    <>
      {renderArticleComparisonNumbering(article_numbering_data)}
      {!isEmpty(block_numbering_data) && NUMBERING_DOT}
      {renderBlockComparisonNumbering(block_numbering_data)}
    </>
  );
};

const VersionComparisonPanelComponent: FC<VersionComparisonPanelProps> = ({
  show,
  onClose,
  currentClaSlug,
  onDocumentTitleChange,
  onComparisonDataChange,
  onVersionsSelected,
}) => {
  const { dictionary } = useContext(LocalizationContext);
  const { reviewModeData } = useBoundStore((state) => ({
    reviewModeData: state.reviewModeData,
  }));

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [versions, setVersions] = useState<CLAVersion[]>([]);
  const [sourceVersionSlug, setSourceVersionSlug] = useState<string>('');
  const [targetVersionSlug, setTargetVersionSlug] = useState<string>('');
  const [sourceIsDraft, setSourceIsDraft] = useState<boolean>(false);
  const [targetIsDraft, setTargetIsDraft] = useState<boolean>(true);
  const [comparisonData, setComparisonData] = useState<ComparisonChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    showRenvoiOnly: false,
    showImportantOnly: false,
    hideRenvoi: false,
    hideImportant: false,
  });
  const [sourceNumbering, setSourceNumbering] = useState<Numbering>({});
  const [targetNumbering, setTargetNumbering] = useState<Numbering>({});

  const prevCurrentClaSlugRef = useRef<string>();
  const prevShowRef = useRef<boolean>();
  const initializationDoneRef = useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const activeReqKeyRef = useRef<string | null>(null);
  const activeAbortRef = useRef<AbortController | null>(null);
  const resolvedKeysRef = useRef<Set<string>>(new Set());
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = useCallback((groupKey: string) => {
    setOpenGroups(prev => {
      const current = prev[groupKey] ?? false;
      return { ...prev, [groupKey]: !current };
    });
  }, []);
  const isGroupOpen = useCallback(
    (groupKey: string) => openGroups[groupKey] ?? false,
    [openGroups],
  );

  const formatArticleForHeader = (arr?: NumberingItem[]) =>
    (arr?.map(i => i?.label ?? '').filter(Boolean).join('.')) || '';

  const formatBlockForHeader = (arr?: NumberingItem[]) =>
    (arr?.map(i => i?.label ?? '').filter(Boolean).join(' • ')) || '';

  const buildHeaderLabel = (parent: GroupParent, change: ComparisonChange) => {
    const sideIsSource = change.change_type === ChangeType.Deleted;
    const map = sideIsSource ? (sourceNumbering as any) : (targetNumbering as any);
    const data = map?.[parent.id as any];
    const articleNumbering = formatArticleForHeader(data?.article_numbering);
    const blockNumbering = formatBlockForHeader(data?.block_numbering);
    let prefix = '';

    if (articleNumbering && blockNumbering) prefix = `${articleNumbering} • ${blockNumbering}`;
    else if (articleNumbering) prefix = `${articleNumbering}.`;
    else if (blockNumbering) prefix = blockNumbering;

    const title = stripHtml(parent.title);
    return prefix ? `${prefix} ${title}` : title;
  };

  const saveScrollPosition = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) scrollPositionRef.current = el.scrollTop;
  }, []);

  const restoreScrollPosition = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: scrollPositionRef.current, behavior: 'instant' as any });
    });
  }, []);

  const versionCacheKey = useMemo(() => currentClaSlug, [currentClaSlug]);
  const comparisonCacheKey = useMemo(
    () => `${sourceVersionSlug}-${targetVersionSlug}-${sourceIsDraft}-${targetIsDraft}`,
    [sourceVersionSlug, targetVersionSlug, sourceIsDraft, targetIsDraft],
  );

  const hasResolvedForCurrentKey =
			resolvedKeysRef.current.has(comparisonCacheKey) ||
			comparisonCache.has(comparisonCacheKey);

  const clearHighlighting = useCallback(() => {
    currentlyHighlightedElements.forEach(el => {
      el.style.outline = '';
      el.style.backgroundColor = '';
    });
    currentlyHighlightedElements = [];
  }, []);

  const setupDoubleClickListener = useCallback(() => {
    const handleDoubleClick = () => {
      clearHighlighting();
    };
    document.addEventListener('dblclick', handleDoubleClick);
    return () => {
      document.removeEventListener('dblclick', handleDoubleClick);
    };
  }, [clearHighlighting]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const cleanup = setupDoubleClickListener();
    return cleanup;
  }, [setupDoubleClickListener]);

  // const getChangeHighlightColor = useCallback((changeType: ChangeType) => {
  //   switch (changeType) {
  //     case ChangeType.Added:
  //       return 'rgba(76, 175, 80, 0.2)';
  //     case ChangeType.Deleted:
  //       return 'rgba(244, 67, 54, 0.2)';
  //     case ChangeType.Modified:
  //       return 'rgba(255, 193, 7, 0.2)';
  //     default:
  //       return 'rgba(158, 158, 158, 0.2)';
  //   }
  // }, []);

  // const getChangeBorderColor = useCallback((changeType: ChangeType) => {
  //   switch (changeType) {
  //     case ChangeType.Added:
  //       return '#4caf50';
  //     case ChangeType.Deleted:
  //       return '#f44336';
  //     case ChangeType.Modified:
  //       return '#ffc107';
  //     default:
  //       return '#9e9e9e';
  //   }
  // }, []);

  const syncDrawerState = useCallback(() => {
    if (show !== prevShowRef.current) {
      setIsDrawerOpen(show);
      prevShowRef.current = show;
    }
  }, [show]);

  useEffect(() => {
    syncDrawerState();
  }, [syncDrawerState]);

  const formatVersionTitle = useCallback((version: CLAVersion) => {
    return `${version.published_at} - PUBLISHED${version.is_current ? ' (current)' : ''}`;
  }, []);

  const formatTargetOptionTitle = useCallback((option: TargetOption) => {
    const dateStr = option.published_at ? option.published_at : '';
    const statusStr = option.isDraft ? 'DRAFT' : 'PUBLISHED';
    let suffixStr = '';
    if (option.isCurrentDraft) {
      suffixStr = ' (current)';
    } else if (option.is_current && !option.isDraft) {
      suffixStr = ' (current)';
    }
    return `${dateStr} - ${statusStr}${suffixStr}`;
  }, []);

  const handleVersionUpdated = useCallback(() => {
    versionCache.clear();
    comparisonCache.clear();
    initializationDoneRef.current = false;
    setIsSettingsDialogOpen(false);

    if (show) {
      fetchVersionHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const initializeFromReviewModeData = useCallback(() => {
    if (show && reviewModeData?.isInitialized && !initializationDoneRef.current) {
      if (reviewModeData.sourceSlug) {
        setSourceVersionSlug(reviewModeData.sourceSlug);
        setSourceIsDraft(reviewModeData.sourceIsDraft);
      }
      if (reviewModeData.targetSlug) {
        setTargetVersionSlug(reviewModeData.targetSlug);
        setTargetIsDraft(reviewModeData.targetIsDraft);
      }
      initializationDoneRef.current = true;

      if (reviewModeData.sourceSlug && reviewModeData.targetSlug) {
        onVersionsSelected?.(
          reviewModeData.sourceSlug,
          reviewModeData.targetSlug,
          reviewModeData.sourceIsDraft,
          reviewModeData.targetIsDraft,
        );
      }
    }
  }, [show, reviewModeData, onVersionsSelected]);

  const fetchVersionHistory = useCallback(async () => {
    if (!currentClaSlug) return;
    const cached = versionCache.get(versionCacheKey);
    if (cached) {
      setVersions(cached.versions);
      if (!initializationDoneRef.current && !reviewModeData?.isInitialized) {
        const currentVersionSlug = cached.current_version || currentClaSlug;
        setSourceVersionSlug(currentVersionSlug);
        setSourceIsDraft(false);
        setTargetVersionSlug(currentVersionSlug);
        setTargetIsDraft(true);
        initializationDoneRef.current = true;

        onVersionsSelected?.(currentVersionSlug, currentVersionSlug, false, true);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await documentsClient.getVersionHistory(currentClaSlug);
      versionCache.set(versionCacheKey, response.data);
      setVersions(response.data.versions || []);

      if (!initializationDoneRef.current && !reviewModeData?.isInitialized) {
        const currentVersionSlug = response.data.current_version || currentClaSlug;
        setSourceVersionSlug(currentVersionSlug);
        setSourceIsDraft(false);
        setTargetVersionSlug(currentVersionSlug);
        setTargetIsDraft(true);
        initializationDoneRef.current = true;

        onVersionsSelected?.(currentVersionSlug, currentVersionSlug, false, true);
      }
    } catch (err) {
      setError('Failed to fetch version history');
    } finally {
      setLoading(false);
    }
  }, [currentClaSlug, versionCacheKey, reviewModeData?.isInitialized, onVersionsSelected]);

  useEffect(() => {
    initializeFromReviewModeData();
  }, [initializeFromReviewModeData]);

  useEffect(() => {
    if (reviewModeData?.needsRefresh) {
      versionCache.clear();
      comparisonCache.clear();
      initializationDoneRef.current = false;
    }
  }, [reviewModeData?.needsRefresh]);

  useEffect(() => {
    if (isDrawerOpen && !loading) {
      restoreScrollPosition();
    }
  }, [isDrawerOpen, loading, restoreScrollPosition]);

  const handleClose = useCallback(() => {
    saveScrollPosition();
    setIsDrawerOpen(false);
    onClose();
  }, [onClose, saveScrollPosition]);

  const buildNumberingStr = (
    entityId: number | string | undefined | null,
    numberingMap: Numbering,
  ) => {
    const data = entityId ? numberingMap?.[entityId as any] : undefined;
    const article = (data as any)?.article_numbering;
    const block = (data as any)?.block_numbering;

    return [
      renderArticleComparisonNumbering(article),
      renderArticleComparisonNumbering(block),
    ]
      .filter(Boolean)
      .join('.');
  };

  const withOrderFromTo = useCallback((
    changes: ComparisonChange[],
    sourceNumbering: Numbering,
    targetNumbering: Numbering,
  ) => {
    return changes.map((ch) => {
      const fromStr = buildNumberingStr(ch.entity_id_source, sourceNumbering);
      const toStr = buildNumberingStr(ch.entity_id_candidate, targetNumbering);

      const hasDelta = !!fromStr && !!toStr && fromStr !== toStr;
      if (!hasDelta || ch.change_type !== ChangeType.Modified) return ch;

      return {
        ...ch,
        change_details: {
          ...(ch.change_details || {}),
          order: { from: fromStr, to: toStr },
        },
      };
    });
  }, []);

  const handleExport = useCallback(async () => {
    if (comparisonData && comparisonData.length > 0) {
      const enriched = withOrderFromTo(comparisonData, sourceNumbering, targetNumbering);
      await handleExportClick(
        enriched,
        versions,
        sourceVersionSlug,
        targetVersionSlug,
        targetIsDraft,
      );
    }
  }, [
    comparisonData,
    versions,
    sourceVersionSlug,
    targetVersionSlug,
    targetIsDraft,
    sourceNumbering,
    targetNumbering,
    withOrderFromTo,
  ]);

  const updateHistoryData = useCallback(() => {
    if (comparisonData && comparisonData.length > 0) {
      const historyData = transformComparisonToHistory({
        changes: comparisonData,
        source_cla: sourceVersionSlug,
        target_cla: targetVersionSlug,
        source_slug: sourceVersionSlug,
        target_slug: targetVersionSlug,
        source_draft: sourceIsDraft,
        target_draft: targetIsDraft,
        total_changes: comparisonData.length,
        source_numbering: {},
        target_numbering: {},
      });
      setHistoryData(historyData);
    }
  }, [comparisonData, sourceVersionSlug, targetVersionSlug, sourceIsDraft, targetIsDraft]);
  useEffect(() => {
    setOpenGroups({});
  }, [comparisonCacheKey]);

  useEffect(() => {
    updateHistoryData();
  }, [updateHistoryData]);

  const shouldFetchVersionHistory = useCallback(() => {
    if (!currentClaSlug) return false;
    if (!versionCache.has(versionCacheKey)) return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (currentClaSlug !== prevCurrentClaSlugRef.current) return true;
    return false;
  }, [currentClaSlug, versionCacheKey]);

  useEffect(() => {
    if (shouldFetchVersionHistory()) {
      fetchVersionHistory();
      prevCurrentClaSlugRef.current = currentClaSlug;
    }
  }, [shouldFetchVersionHistory, fetchVersionHistory, currentClaSlug]);

  const getCurrentVersionSlug = useCallback(() => {
    return versions.find(v => v.is_current)?.slug || currentClaSlug;
  }, [versions, currentClaSlug]);

  const getSourceOptions = useMemo(() => {
    return versions.filter(version => version.status === 'published' || version.status === 'approved');
  }, [versions]);

  const getTargetOptions = useMemo((): TargetOption[] => {
    const options: TargetOption[] = [];
    const currentVersionSlug = getCurrentVersionSlug();
    if (!(sourceVersionSlug === currentVersionSlug && sourceIsDraft)) {
      options.push({
        slug: currentVersionSlug,
        isDraft: true,
        title: 'Draft version',
        isCurrentDraft: true,
      });
    }
    versions
      .filter(version => version.status === 'published' || version.status === 'approved')
      .forEach(version => {
        if (!(sourceVersionSlug === version.slug && !sourceIsDraft)) {
          options.push({
            slug: version.slug,
            isDraft: false,
            title: version.title,
            published_at: version.published_at,
            created_at: version.created_at,
            is_current: version.is_current,
          });
        }
      });
    return options;
  }, [versions, sourceVersionSlug, sourceIsDraft, getCurrentVersionSlug]);

  const canCompare = useCallback(() => {
    return !(sourceVersionSlug === targetVersionSlug && sourceIsDraft === targetIsDraft);
  }, [sourceVersionSlug, targetVersionSlug, sourceIsDraft, targetIsDraft]);

  const handleSourceVersionChange = useCallback((newSlug: string) => {
    setSourceVersionSlug(newSlug);
    setSourceIsDraft(false);
    onVersionsSelected?.(newSlug, targetVersionSlug, false, targetIsDraft);
    const currentVersionSlug = getCurrentVersionSlug();
    if (newSlug === currentVersionSlug && targetVersionSlug === currentVersionSlug && targetIsDraft) {
      const otherPublishedVersion = versions.find(v =>
        v.slug !== currentVersionSlug && (v.status === 'published' || v.status === 'approved'),
      );
      if (otherPublishedVersion) {
        setTargetVersionSlug(otherPublishedVersion.slug);
        setTargetIsDraft(false);
        onVersionsSelected?.(newSlug, otherPublishedVersion.slug, false, false);
      }
    }
    if (newSlug === targetVersionSlug && !targetIsDraft) {
      if (newSlug !== currentVersionSlug) {
        setTargetVersionSlug(currentVersionSlug);
        setTargetIsDraft(true);
        onVersionsSelected?.(newSlug, currentVersionSlug, false, true);
      } else {
        const otherPublishedVersion = versions.find(v =>
          v.slug !== newSlug && (v.status === 'published' || v.status === 'approved'),
        );
        if (otherPublishedVersion) {
          setTargetVersionSlug(otherPublishedVersion.slug);
          setTargetIsDraft(false);
          onVersionsSelected?.(newSlug, otherPublishedVersion.slug, false, false);
        }
      }
    }
  }, [getCurrentVersionSlug, versions, targetVersionSlug, targetIsDraft, onVersionsSelected]);

  const handleTargetVersionChange = useCallback((newSlug: string, isDraft: boolean) => {
    setTargetVersionSlug(newSlug);
    setTargetIsDraft(isDraft);
    onVersionsSelected?.(sourceVersionSlug, newSlug, sourceIsDraft, isDraft);
  }, [sourceVersionSlug, sourceIsDraft, onVersionsSelected]);

  const fetchComparison = useCallback(async () => {
    if (!sourceVersionSlug || !targetVersionSlug) return;
    if (sourceVersionSlug === targetVersionSlug && sourceIsDraft === targetIsDraft) return;

    const reqKey = `${sourceVersionSlug}-${targetVersionSlug}-${sourceIsDraft}-${targetIsDraft}`;

    const cached = comparisonCache.get(reqKey) as any | undefined;
    if (cached) {
      setComparisonData(cached.changes || []);
      setSourceNumbering(cached.source_numbering || {});
      setTargetNumbering(cached.target_numbering || {});
      onComparisonDataChange?.(cached.changes?.length || 0);
      resolvedKeysRef.current.add(reqKey);
    }

    if (activeAbortRef.current) {
      try {
        activeAbortRef.current.abort();
      } catch {
      }
    }

    const ctrl = new AbortController();
    activeAbortRef.current = ctrl;
    activeReqKeyRef.current = reqKey;

    setError(null);
    setLoading(true);

    const lastDone = comparisonLastDoneAt.get(reqKey);
    if (lastDone && Date.now() - lastDone < 600) {
      if (activeReqKeyRef.current === reqKey) {
        setLoading(false);
        activeAbortRef.current = null;
      }
      return;
    }

    try {
      const params = {
        sourceSlug: sourceVersionSlug,
        targetSlug: targetVersionSlug,
        sourceDraft: sourceIsDraft,
        targetDraft: targetIsDraft,
      };

      const res = await documentsClient.compareVersions(params, { signal: ctrl.signal });

      setComparisonData(res.data.changes || []);
      setSourceNumbering(res.data.source_numbering || {});
      setTargetNumbering(res.data.target_numbering || {});

      if (activeReqKeyRef.current !== reqKey) return;

      comparisonCache.set(reqKey, res.data);
      comparisonLastDoneAt.set(reqKey, Date.now());

      setComparisonData(res.data.changes || []);
      onComparisonDataChange?.(res.data.changes?.length || 0);

      resolvedKeysRef.current.add(reqKey);

      const sourceVersion = versions.find(v => v.slug === sourceVersionSlug);
      const targetOption = getTargetOptions.find(opt => opt.slug === targetVersionSlug && opt.isDraft === targetIsDraft);

      const sourceTitle = sourceVersion
        ? formatVersionTitle(sourceVersion)
        : `${sourceVersionSlug} (${sourceIsDraft ? 'draft' : 'published'})`;

      const targetTitle = targetOption
        ? formatTargetOptionTitle(targetOption)
        : (targetIsDraft && targetVersionSlug === getCurrentVersionSlug()
          ? 'Draft version'
          : `${targetVersionSlug} (${targetIsDraft ? 'draft' : 'published'})`);

      onDocumentTitleChange?.(sourceTitle, targetTitle);
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' || err?.message === 'canceled') {
        return;
      }
      if (activeReqKeyRef.current === reqKey) {
        setError('Failed to fetch comparison data');
      }
    } finally {
      if (activeReqKeyRef.current === reqKey) {
        setLoading(false);
        activeAbortRef.current = null;
      }
    }
  }, [
    sourceVersionSlug, targetVersionSlug, sourceIsDraft, targetIsDraft,
    versions, getTargetOptions, getCurrentVersionSlug,
    formatVersionTitle, formatTargetOptionTitle,
    onComparisonDataChange, onDocumentTitleChange,
  ]);


  const debouncedFetchComparison = useMemo(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    const fn = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fetchComparison();
      }, 300);
    };
    (fn as any).cancel = () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    return fn;
  }, [fetchComparison]);

  useEffect(() => {
    return () => {
      (debouncedFetchComparison as any).cancel?.();
    };
  }, [debouncedFetchComparison]);


  useEffect(() => {
    if (!sourceVersionSlug || !targetVersionSlug || !canCompare()) return;

    const cached = comparisonCache.get(comparisonCacheKey) as any | undefined;

    if (cached) {
      setComparisonData(cached.changes || []);
      setSourceNumbering(cached.source_numbering || {});
      setTargetNumbering(cached.target_numbering || {});
      onComparisonDataChange?.(cached.changes?.length || 0);
    } else {
      setComparisonData([]);
      setSourceNumbering({});
      setTargetNumbering({});
    }
  }, [
    comparisonCacheKey,
    sourceVersionSlug,
    targetVersionSlug,
    canCompare,
    onComparisonDataChange,
  ]);

  useEffect(() => {
    if (sourceVersionSlug && targetVersionSlug && show && canCompare() && !hasResolvedForCurrentKey) {
      debouncedFetchComparison();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceVersionSlug, targetVersionSlug, sourceIsDraft, targetIsDraft, canCompare, hasResolvedForCurrentKey, debouncedFetchComparison]);

  const isRenvoiChange = useCallback((change: ComparisonChange): boolean => {
    return change.is_renvoi_change || false;
  }, []);

  const isImportantChange = useCallback((change: ComparisonChange): boolean => {
    return change.is_important_change || false;
  }, []);

  const filteredChanges = useMemo(() => {
    return comparisonData.filter(change => {
      const isRenvoi = isRenvoiChange(change);
      const isImportant = isImportantChange(change);
      if (filters.hideRenvoi && isRenvoi) return false;
      if (filters.hideImportant && isImportant) return false;
      if (filters.showRenvoiOnly && !isRenvoi) return false;
      if (filters.showImportantOnly && !isImportant) return false;
      const sourceTitle = change.title_source || '';
      const targetTitle = change.title_candidate || '';
      const sourceText = change.text_source || '';
      const targetText = change.text_candidate || '';
      if (change.change_type === ChangeType.Added) {
        if (!targetTitle && !targetText) return false;
      }
      if (change.change_type === ChangeType.Deleted) {
        if (!sourceTitle && !sourceText) return false;
      }
      return true;
    });
  }, [comparisonData, filters, isRenvoiChange, isImportantChange]);

  const queryIn = (root: HTMLElement | null, selector: string) =>
    root ? Array.from(root.querySelectorAll<HTMLElement>(selector)) : [];


  type FieldKey = 'title' | 'subtitle' | 'text' | 'json_content';
  const scrollToElement = useCallback((change: ComparisonChange, opts?: { changeType?: ChangeType; field?: FieldKey }) => {
    saveScrollPosition();
    const sourceRoot = document.getElementById(SOURCE_CONTAINER_ID);
    const targetRoot = document.getElementById(TARGET_CONTAINER_ID);

    const effectiveType = opts?.changeType ?? change.change_type;
    let wantSource = false;
    let wantTarget = false;

    switch (effectiveType) {
      case ChangeType.Added:
        wantTarget = true;
        break;
      case ChangeType.Deleted:
        wantSource = true;
        break;
      case ChangeType.Modified:
        wantSource = !!change.entity_id_source;
        wantTarget = !!change.entity_id_candidate;
        break;
    }

    const srcSelector = change.entity_id_source
      ? `[data-${change.entity_type}-id="${change.entity_id_source}"]`
      : '';

    const tgtSelector = change.entity_id_candidate
      ? `[data-${change.entity_type}-id="${change.entity_id_candidate}"]`
      : '';

    let srcMatches: HTMLElement[] = [];
    let tgtMatches: HTMLElement[] = [];

    if (wantSource && srcSelector) srcMatches = queryIn(sourceRoot, srcSelector);
    if (wantTarget && tgtSelector) tgtMatches = queryIn(targetRoot, tgtSelector);
    if (wantSource && srcMatches.length === 0) {
      let idSel = '';
      if ('json_content' in (change?.change_details || {}) || !change.slug_source) {
        idSel = `#spreadsheet-block-${change.entity_id_source}`;
      } else {
        idSel = `#${change.slug_source.replace(/\./g, '\\.')}`;
      }

      srcMatches = queryIn(sourceRoot, idSel);
    }
    if (wantTarget && tgtMatches.length === 0 && change.slug_candidate) {
      let idSel = `#${change.slug_candidate.replace(/\./g, '\\.')}`;
      if ('json_content' in (change?.change_details || {})) {
        idSel = `#spreadsheet-block-${change.entity_id_source}`;
      }
      tgtMatches = queryIn(targetRoot, idSel);
    }

    if ((wantSource && srcMatches.length === 0) && (wantTarget && tgtMatches.length === 0)) {
      return;
    }

    clearHighlighting();

    if (srcMatches[0]) {
      srcMatches[0].scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    if (tgtMatches[0]) {
      tgtMatches[0].scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    // const paintOnlyDirectElement = (el: HTMLElement, changeType: ChangeType) => {
    //   const borderColor = getChangeBorderColor(changeType);
    //   const backgroundColor = getChangeHighlightColor(changeType);
    //
    //   const directChild = el.querySelector('.document-child-item > *:first-child') as HTMLElement;
    //   const targetElement = directChild || el;
    //
    //   targetElement.style.outline = `2px solid ${borderColor}`;
    //   targetElement.style.backgroundColor = backgroundColor;
    //
    //   const childItems = el.querySelectorAll('.document-child-item .document-child-item');
    //   childItems.forEach((child) => {
    //     (child as HTMLElement).style.outline = '';
    //     (child as HTMLElement).style.backgroundColor = '';
    //   });
    //
    //   currentlyHighlightedElements.push(targetElement);
    // };
    //
    // srcMatches.forEach(el => paintOnlyDirectElement(el, change.change_type));
    // tgtMatches.forEach(el => paintOnlyDirectElement(el, change.change_type));

    handleClose();
  }, [clearHighlighting, handleClose, saveScrollPosition]);


  const getChangeIcon = useCallback((changeType: ChangeType) => {
    switch (changeType) {
      case ChangeType.Added:
        return <Add sx={{ fill: '#fff', fontSize: 13, width: 14, height: 14 }}/>;
      case ChangeType.Deleted:
        return <Close sx={{ fill: '#fff', fontSize: 13, width: 14, height: 14 }}/>;
      case ChangeType.Modified:
        return <LoopIcon sx={{ fill: '#fff', fontSize: 13, width: 14, height: 14 }}/>;
      default:
        return null;
    }
  }, []);

  const getChangeColor = useCallback((changeType: ChangeType) => {
    switch (changeType) {
      case ChangeType.Added:
        return '#3da447';
      case ChangeType.Deleted:
        return '#bc262c';
      case ChangeType.Modified:
        return '#d5812a';
      default:
        return '#757575';
    }
  }, []);


  const maxArticleNumberWidth = (() => {
    const maxCount = filteredChanges.reduce((max, c) => {
      const useCandidate = c.change_type !== ChangeType.Deleted;
      const article = (useCandidate ? c.article_numbering_candidate : c.article_numbering_source) ?? [];
      const block = (useCandidate ? c.block_numbering_candidate : c.block_numbering_source) ?? [];
      return Math.max(max, article.length + block.length);
    }, 0);
    return `${Math.max(maxCount * 18, 32)}px`;
  })();

  const renderChangeItem = useCallback(
    (
      change: ComparisonChange,
      index: number,
      sourceNumbering: Numbering,
      targetNumbering: Numbering,
    ) => {
      const FIELDS: Array<'title' | 'subtitle' | 'text' | 'json_content'> = ['title', 'subtitle', 'text', 'json_content'];
      const footNoteStyles = {
        display: 'inline',
        backgroundImage: 'linear-gradient(to right, rgba(0, 0, 0, 0) 50%, lightgrey 50%)',
        backgroundSize: '4px 2px',
        backgroundRepeat: 'repeat-x',
        backgroundPosition: '0 100%',
        textDecoration: 'none',
        pointerEvents: 'none',
        cursor: 'default',
        marginTop: '5px',
      };
      const mapStrToChangeType = (string?: string): ChangeType => {
        if (!string) return ChangeType.Modified;
        if (string === 'added') return ChangeType.Added;
        if (string === 'deleted') return ChangeType.Deleted;
        return ChangeType.Modified;
      };
      const getScopedFootnotes = (field: 'title' | 'subtitle' | 'text' | 'json_content') => {
        const changeDetails: any = change.change_details || {};
        const scoped = changeDetails?.[`footnotes_${field}`];
        const from = scoped?.from ?? scoped?.source ?? [];
        const to = scoped?.to ?? scoped?.target ?? [];
        const changeType = scoped?.change_type as 'added' | 'removed' | 'modified' | undefined;
        return { from: Array.isArray(from) ? from : [], to: Array.isArray(to) ? to : [], changeType };
      };
      const createHtmlProps = (content: string) => ({
        dangerouslySetInnerHTML: { __html: content || '' },
      });

      const items = FIELDS.map((field, subIdx) => {
        const details: any = change.change_details || {};

        const jsonWins = !!details?.json_content?.change_type;
        if (jsonWins && field !== 'json_content' && change.change_type !== 'modified' ) return null;

        const item = details?.[field];
        if (!item) return null;

        const fieldChangeType = mapStrToChangeType(item.change_type);
        const isJsonField = field === 'json_content';
        const rawFromStr = isJsonField ? '' : (item.from || '');
        const rawToStr = isJsonField ? '' : (item.to || '');
        const fromStr = markInvalidAnchors(rawFromStr);
        const toStr = markInvalidAnchors(rawToStr);
        const { from: footFrom, to: footTo, changeType: footCT } = getScopedFootnotes(field);

        const colorTo = footCT === 'added' ? ADDED_FOOTNOTE_COLOR : DEFAULT_TO_COLOR;
        const colorFrom = footCT === 'removed' ? REMOVED_FOOTNOTE_COLOR : DEFAULT_FROM_COLOR;
        const sourceEntityId = change.entity_id_source;
        const targetEntityId = change.entity_id_candidate;
        const sourceNumberingData = sourceEntityId && sourceNumbering?.[sourceEntityId];
        const targetNumberingData = targetEntityId && targetNumbering?.[targetEntityId];

        const sourceArticleNumbering = (sourceNumberingData as any)?.article_numbering;
        const sourceBlockNumbering = (sourceNumberingData as any)?.block_numbering;
        const targetArticleNumbering = (targetNumberingData as any)?.article_numbering;
        const targetBlockNumbering = (targetNumberingData as any)?.block_numbering;

        const sourceNumberingStr = [
          renderArticleComparisonNumbering(sourceArticleNumbering),
          renderArticleComparisonNumbering(sourceBlockNumbering),
        ]
          .filter(Boolean)
          .join('.');

        const targetNumberingStr = [
          renderArticleComparisonNumbering(targetArticleNumbering),
          renderArticleComparisonNumbering(targetBlockNumbering),
        ]
          .filter(Boolean)
          .join('.');

        const hasNumberingChange =
							sourceNumberingStr !== targetNumberingStr && sourceNumberingStr && targetNumberingStr;

        const renderContent = () => {
          switch (fieldChangeType) {
            case ChangeType.Added:
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: '#424242',
                      lineHeight: 1.3,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      flex: 1,
                      '& *': {
                        fontSize: '0.8rem !important',
                        lineHeight: '1.3 !important',
                        margin: '0 !important',
                        padding: '0 !important',
                        maxWidth: '100% !important',
                        color: 'inherit !important',
                      },
                      '& a': { textDecoration: 'underline', color: 'inherit !important' },
                      '& br': { display: 'none' },
                      '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                    }}
                    {...createHtmlProps(toStr)}
                  />
                  {isJsonField && 
                    <Typography sx={{
                      display: 'flex', alignItems: 'center', gap: 1, mt: 0.5,
                    }}>
                      <BackupTableIcon />
                      Table was added
                    </Typography>
                  }
                  {footTo.map((fnChange: any, fnIndex: number) => (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: fnIndex === 0 ? '4px' : 0 }}
                      key={
                        `fn-box-${index}-${subIdx}-to-${fnIndex}` + (fnChange.footnote || fnIndex)
                      }
                    >
                      <Tooltip title={fnChange.title || ''}>
                        <InfoOutlinedIcon/>
                      </Tooltip>
                      <Typography
                        sx={{ fontSize: '0.75rem', '& p': { margin: 0 }, color: colorTo }}
                        {...createHtmlProps(fnChange.footnote || '')}
                      />
                    </Box>
                  ))}
                </Box>
              );
            case ChangeType.Deleted:
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: '#424242',
                      lineHeight: 1.3,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      flex: 1,
                      '& *': {
                        fontSize: '0.8rem !important',
                        lineHeight: '1.3 !important',
                        margin: '0 !important',
                        padding: '0 !important',
                        maxWidth: '100% !important',
                        color: 'inherit !important',
                      },
                      '& a': { textDecoration: 'underline', color: 'inherit !important' },
                      '& br': { display: 'none' },
                      '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                    }}
                    {...createHtmlProps(fromStr)}
                  />
                  {isJsonField && 
                    <Typography sx={{
                      display: 'flex', alignItems: 'center', gap: 1, mt: 0.5,
                    }}>
                      <BackupTableIcon />
                      Table was removed
                    </Typography>
                  }
                  {footFrom.map((fnChange: any, fnIndex: number) => (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: fnIndex === 0 ? '4px' : 0 }}
                      key={
                        `fn-box-${index}-${subIdx}-from-${fnIndex}` + (fnChange.footnote || fnIndex)
                      }
                    >
                      <Tooltip title={fnChange.title || ''}>
                        <InfoOutlinedIcon style={{ color: '#9e9e9e', fill: '#9e9e9e' }}/>
                      </Tooltip>
                      <Typography
                        sx={{ fontSize: '0.75rem', color: colorFrom, '& p': { margin: 0 } }}
                        {...createHtmlProps(fnChange.footnote || '')}
                      />
                    </Box>
                  ))}
                </Box>
              );
            case ChangeType.Modified:
            default:
              return (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  {hasNumberingChange && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        alignItems: 'center',
                        mb: 0.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: '#424242',
                          lineHeight: 1.3,
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          fontStyle: 'normal !important',
                          '& *': {
                            fontSize: '0.8rem !important',
                            lineHeight: '1.3 !important',
                            margin: '0 !important',
                            padding: '0 !important',
                            maxWidth: '100% !important',
                            color: 'inherit !important',
                          },
                          '& a': { textDecoration: 'underline', color: 'inherit !important' },
                          '& br': { display: 'none' },
                          '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                        }}
                      >
                        {sourceNumberingStr}
                      </Typography>
                      <Typography>{'->'}</Typography>
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: '#424242',
                          lineHeight: 1.3,
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          fontStyle: 'normal !important',
                          '& *': {
                            fontSize: '0.8rem !important',
                            lineHeight: '1.3 !important',
                            margin: '0 !important',
                            padding: '0 !important',
                            maxWidth: '100% !important',
                            color: 'inherit !important',
                          },
                          '& a': { textDecoration: 'underline', color: 'inherit !important' },
                          '& br': { display: 'none' },
                          '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                        }}
                      >
                        {targetNumberingStr}
                      </Typography>
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: '#9e9e9e',
                      lineHeight: 1.3,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      '& *': {
                        fontSize: '0.8rem !important',
                        lineHeight: '1.3 !important',
                        margin: '0 !important',
                        padding: '0 !important',
                        maxWidth: '100% !important',
                        color: 'inherit !important',
                      },
                      '& a': { textDecoration: 'underline', color: 'inherit !important' },
                      '& br': { display: 'none' },
                      '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                    }}
                    {...createHtmlProps(fromStr)}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: '#424242',
                      lineHeight: 1.3,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      '& *': {
                        fontSize: '0.8rem !important',
                        lineHeight: '1.3 !important',
                        margin: '0 !important',
                        padding: '0 !important',
                        maxWidth: '100% !important',
                        color: 'inherit !important',
                      },
                      '& a': { color: 'inherit !important' },
                      '& br': { display: 'none' },
                      '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                    }}
                    {...createHtmlProps(toStr)}
                  />
                  {isJsonField && 
                    <Typography sx={{
                      display: 'flex', alignItems: 'center', gap: 1, mt: 0.5,
                    }}>
                      <BackupTableIcon />
                      Table was modified
                    </Typography>
                  }
                  {footFrom.map((fnChange: any, fnIndex: number) => (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: fnIndex === 0 ? '4px' : 0 }}
                      key={
                        `fn-box-${index}-${subIdx}-from-${fnIndex}` + (fnChange.footnote || fnIndex)
                      }
                    >
                      <Tooltip title={fnChange.title || ''}>
                        <InfoOutlinedIcon style={{ color: '#9e9e9e', fill: '#9e9e9e' }}/>
                      </Tooltip>
                      <Typography
                        sx={{ fontSize: '0.75rem', color: colorFrom, '& p': { margin: 0 } }}
                        {...createHtmlProps(fnChange.footnote || '')}
                      />
                    </Box>
                  ))}
                  {footTo.map((fnChange: any, fnIndex: number) => (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: fnIndex === 0 ? '4px' : 0 }}
                      key={
                        `fn-box-${index}-${subIdx}-to-${fnIndex}` + (fnChange.footnote || fnIndex)
                      }
                    >
                      <Tooltip title={fnChange.title || ''}>
                        <InfoOutlinedIcon/>
                      </Tooltip>
                      <Typography
                        sx={{ fontSize: '0.75rem', '& p': { margin: 0 }, color: colorTo }}
                        {...createHtmlProps(fnChange.footnote || '')}
                      />
                    </Box>
                  ))}
                </Box>
              );
          }
        };

        return (
          <ListItem
            key={`${change.version_reference_id}-${index}-${field}`}
            onClick={() => scrollToElement(change, { changeType: fieldChangeType, field })}
            sx={{
              px: 0,
              py: 0,
              cursor: 'pointer',
              mb: 1,
              borderRadius: 1,
              backgroundColor: '#f8f9fa',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            <Box
              sx={{
                width: '100%',
                p: 1.5,
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                alignItems: 'flex-start',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  backgroundColor: getChangeColor(fieldChangeType),
                  color: 'white',
                  borderRadius: '50%',
                  flexShrink: 0,
                }}
              >
                {getChangeIcon(fieldChangeType)}
              </Box>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  color: '#1a1a1a',
                  fontSize: '0.875rem',
                  flexShrink: 0,
                  width: maxArticleNumberWidth,
                  textAlign: 'left',
                }}
              >
                {fieldChangeType === ChangeType.Deleted ? (
                  <RenderNumbering
                    article_numbering_data={change.article_numbering_source ?? []}
                    block_numbering_data={change.block_numbering_source ?? []}
                  />
                ) : (
                  <RenderNumbering
                    article_numbering_data={change.article_numbering_candidate ?? []}
                    block_numbering_data={change.block_numbering_candidate ?? []}
                  />
                )}
              </Typography>

              {renderContent()}

              {change.is_renvoi_change && (
                <Box
                  title={dictionary.versionComparison.filterByRenvoiChanges}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    minWidth: 24,
                    height: 24,
                    backgroundColor: 'none',
                    color: '#222',
                    borderRadius: '50%',
                    fontSize: '0.6rem',
                    fontWeight: 400,
                    cursor: 'pointer',
                    border: '#222 1px solid',
                    flexShrink: 0,
                    textDecoration: 'line-through',
                  }}
                >
												R
                </Box>
              )}
              {change.is_important_change && (
                <Box
                  title={dictionary.versionComparison.filterByImportantChanges}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    minWidth: 24,
                    height: 24,
                    backgroundColor: 'none',
                    color: '#222',
                    borderRadius: '50%',
                    fontSize: '0.6rem',
                    fontWeight: 400,
                    cursor: 'pointer',
                    border: '#222 1px solid',
                    flexShrink: 0,
                  }}
                >
												A
                </Box>
              )}
            </Box>
          </ListItem>
        );
      }).filter(Boolean);

      // Do not render fallback for json_content changes (tables)
      if (items.length === 0 && !('json_content' in (change.change_details || {}))) {
        const topType = change.change_type;
        const sourceEntityId = change.entity_id_source;
        const targetEntityId = change.entity_id_candidate;

        const sourceNumberingData = sourceEntityId && sourceNumbering?.[sourceEntityId];
        const targetNumberingData = targetEntityId && targetNumbering?.[targetEntityId];

        const sourceArticleNumbering = (sourceNumberingData as any)?.article_numbering;
        const sourceBlockNumbering = (sourceNumberingData as any)?.block_numbering;
        const targetArticleNumbering = (targetNumberingData as any)?.article_numbering;
        const targetBlockNumbering = (targetNumberingData as any)?.block_numbering;

        const sourceNumberingStr = [
          renderArticleComparisonNumbering(sourceArticleNumbering),
          renderArticleComparisonNumbering(sourceBlockNumbering),
        ]
          .filter(Boolean)
          .join('.');

        const targetNumberingStr = [
          renderArticleComparisonNumbering(targetArticleNumbering),
          renderArticleComparisonNumbering(targetBlockNumbering),
        ]
          .filter(Boolean)
          .join('.');

        const hasNumberingChange =
							sourceNumberingStr && targetNumberingStr && sourceNumberingStr !== targetNumberingStr;

        const beforeHtml = markInvalidAnchors(change.text_source || '');
        const afterHtml = markInvalidAnchors(change.text_candidate || '');

        return (
          <ListItem
            key={`${change.version_reference_id}-${index}-fallback`}
            onClick={() => scrollToElement(change)}
            sx={{
              px: 0,
              py: 0,
              cursor: 'pointer',
              mb: 1,
              borderRadius: 1,
              backgroundColor: '#f8f9fa',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            <Box
              sx={{
                width: '100%',
                p: 1.5,
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                alignItems: 'flex-start',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  backgroundColor: getChangeColor(topType),
                  color: 'white',
                  borderRadius: '50%',
                  flexShrink: 0,
                }}
              >
                {getChangeIcon(topType)}
              </Box>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  color: '#1a1a1a',
                  fontSize: '0.875rem',
                  flexShrink: 0,
                  width: maxArticleNumberWidth,
                  textAlign: 'left',
                }}
              >
                {topType === ChangeType.Deleted ? (
                  <RenderNumbering
                    article_numbering_data={change.article_numbering_source ?? []}
                    block_numbering_data={change.block_numbering_source ?? []}
                  />
                ) : (
                  <RenderNumbering
                    article_numbering_data={change.article_numbering_candidate ?? []}
                    block_numbering_data={change.block_numbering_candidate ?? []}
                  />
                )}
              </Typography>

              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {hasNumberingChange && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      alignItems: 'center',
                      mb: 0.5,
                      fontSize: '0.75rem',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        color: '#424242',
                        lineHeight: 1.3,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        fontStyle: 'normal !important',
                        '& *': {
                          fontSize: '0.8rem !important',
                          lineHeight: '1.3 !important',
                          margin: '0 !important',
                          padding: '0 !important',
                          maxWidth: '100% !important',
                          color: 'inherit !important',
                        },
                        '& a': { textDecoration: 'underline !important', color: 'inherit !important' },
                        '& br': { display: 'none' },
                        '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                      }}
                    >
                      {sourceNumberingStr}
                    </Typography>
                    <Typography>{'->'}</Typography>
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        color: '#424242',
                        lineHeight: 1.3,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        fontStyle: 'normal !important',
                        '& *': {
                          fontSize: '0.8rem !important',
                          lineHeight: '1.3 !important',
                          margin: '0 !important',
                          padding: '0 !important',
                          maxWidth: '100% !important',
                          color: 'inherit !important',
                        },
                        '& a': { textDecoration: 'underline !important', color: 'inherit !important' },
                        '& br': { display: 'none' },
                        '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                      }}
                    >
                      {targetNumberingStr}
                    </Typography>
                  </Box>
                )}

                {topType !== ChangeType.Added && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: '#9e9e9e',
                      lineHeight: 1.3,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      '& *': {
                        fontSize: '0.8rem !important',
                        lineHeight: '1.3 !important',
                        margin: '0 !important',
                        padding: '0 !important',
                        maxWidth: '100% !important',
                        color: 'inherit !important',
                      },
                      '& a': { textDecoration: 'underline !important', color: 'inherit !important' },
                      '& br': { display: 'none' },
                      '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                    }}
                    dangerouslySetInnerHTML={{ __html: beforeHtml }}
                  />
                )}

                {topType !== ChangeType.Deleted && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: '#424242',
                      lineHeight: 1.3,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      '& *': {
                        fontSize: '0.8rem !important',
                        lineHeight: '1.3 !important',
                        margin: '0 !important',
                        padding: '0 !important',
                        maxWidth: '100% !important',
                        color: 'inherit !important',
                      },
                      '& a': { textDecoration: 'underline !important', color: 'inherit !important' },
                      '& br': { display: 'none' },
                      '& a[data-invalid-footnote="1"]': { ...footNoteStyles },
                    }}
                    dangerouslySetInnerHTML={{ __html: afterHtml }}
                  />
                )}
              </Box>

              {change.is_renvoi_change && (
                <Box
                  title={dictionary.versionComparison.filterByRenvoiChanges}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    minWidth: 24,
                    height: 24,
                    backgroundColor: 'none',
                    color: '#222',
                    borderRadius: '50%',
                    fontSize: '0.6rem',
                    fontWeight: 400,
                    cursor: 'pointer',
                    border: '#222 1px solid',
                    flexShrink: 0,
                    textDecoration: 'line-through',
                  }}
                >
												R
                </Box>
              )}
              {change.is_important_change && (
                <Box
                  title={dictionary.versionComparison.filterByImportantChanges}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    minWidth: 24,
                    height: 24,
                    backgroundColor: 'none',
                    color: '#222',
                    borderRadius: '50%',
                    fontSize: '0.6rem',
                    fontWeight: 400,
                    cursor: 'pointer',
                    border: '#222 1px solid',
                    flexShrink: 0,
                  }}
                >
												A
                </Box>
              )}
            </Box>
          </ListItem>
        );
      }

      return <>{items}</>;
    },
    [
      scrollToElement,
      getChangeColor,
      getChangeIcon,
      maxArticleNumberWidth,
      dictionary.versionComparison.filterByRenvoiChanges,
      dictionary.versionComparison.filterByImportantChanges,
    ],
  );
  if (!show) return null;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0',
      }}>
        <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '1.1rem', color: '#1a1a1a' }}>
          {dictionary.versionComparison.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setIsSettingsDialogOpen(true)}
            size="small"
            sx={{ color: '#666' }}
            title="Settings"
          >
            <Settings sx={{ fontSize: 18 }}/>
          </IconButton>
          <IconButton onClick={handleClose} size="small" sx={{ color: '#666' }}>
            <Close sx={{ fontSize: 18 }}/>
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <Select
                value={sourceVersionSlug || ''}
                onChange={(e) => handleSourceVersionChange(e.target.value as string)}
                sx={{ backgroundColor: 'white', fontSize: '0.875rem' }}
                displayEmpty
              >
                {!sourceVersionSlug && (
                  <MenuItem value="" disabled>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {dictionary.versionComparison.selectSourceVersion}
                    </Typography>
                  </MenuItem>
                )}
                {getSourceOptions.map((version) => (
                  <MenuItem key={version.slug} value={version.slug}>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {version.published_at} - PUBLISHED
                      {version.is_current && ' (current)'}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <Select
                value={targetVersionSlug ? `${targetVersionSlug}-${targetIsDraft}` : ''}
                onChange={(e) => {
                  const value = e.target.value as string;
                  if (!value) return;
                  const lastDashIndex = value.lastIndexOf('-');
                  const slug = value.substring(0, lastDashIndex);
                  const isDraftStr = value.substring(lastDashIndex + 1);
                  const isDraft = isDraftStr === 'true';
                  handleTargetVersionChange(slug, isDraft);
                }}
                sx={{ backgroundColor: 'white', fontSize: '0.875rem' }}
                displayEmpty
              >
                {!targetVersionSlug && (
                  <MenuItem value="" disabled>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {dictionary.versionComparison.selectTargetVersion}
                    </Typography>
                  </MenuItem>
                )}
                {getTargetOptions.map((option) => (
                  <MenuItem key={`${option.slug}-${option.isDraft}`} value={`${option.slug}-${option.isDraft}`}>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {option.published_at ? option.published_at : ''} - {option.isDraft ? 'DRAFT' : 'PUBLISHED'}
                      {option.isCurrentDraft && ' (current)'}
                      {option.is_current && !option.isDraft && ' (current)'}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <Box
            onClick={() => setFilters(prev => ({
              ...prev,
              showRenvoiOnly: !prev.showRenvoiOnly,
              showImportantOnly: false,
            }))}
            sx={{
              textDecoration: 'line-through',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              backgroundColor: filters.showRenvoiOnly ? '#1565c0' : 'none',
              color: filters.showRenvoiOnly ? 'white' : '#222',
              borderRadius: '50%',
              fontSize: '0.6rem',
              fontWeight: 400,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              border: filters.showRenvoiOnly ? 'none' : '#222 1px solid',
              '&:hover': { backgroundColor: '#1565c0', border: 'none', color: 'white', transform: 'scale(1.1)' },
            }}
            title="Filter by Renvoi changes"
          >
							R
          </Box>
          <Box
            onClick={() => setFilters(prev => ({
              ...prev,
              showImportantOnly: !prev.showImportantOnly,
              showRenvoiOnly: false,
            }))}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              backgroundColor: filters.showImportantOnly ? '#1565c0' : 'none',
              color: filters.showImportantOnly ? 'white' : '#222',
              borderRadius: '50%',
              fontSize: '0.6rem',
              fontWeight: 400,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              border: filters.showImportantOnly ? 'none' : '#222 1px solid',
              '&:hover': { backgroundColor: '#1565c0', border: 'none', color: 'white', transform: 'scale(1.1)' },
            }}
            title="Filter by Important changes"
          >
							A
          </Box>
          <Button variant="contained" onClick={handleExport} disabled={loading || !comparisonData.length}
            sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.875rem', fontWeight: 600 }}>
            {dictionary.versionComparison.export}
          </Button>
        </Box>
      </Box>
      <Box sx={{ backgroundColor: '#f8f9fa', flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box ref={scrollContainerRef} sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0, px: 2, pb: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24}/>
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!loading && !error && hasResolvedForCurrentKey && filteredChanges.length === 0 && comparisonData.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {dictionary.versionComparison.noChangesMatchingFilters}
            </Alert>
          )}
          {!loading && !error && hasResolvedForCurrentKey && comparisonData.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {dictionary.versionComparison.noChangesFoundBetweenVersions}
            </Alert>
          )}
          {filteredChanges.map((change, index) => {
            const prevParents = index > 0 ? getGroupingParents(filteredChanges[index - 1]) : [];
            const currParents = getGroupingParents(change);
            const headers = headersForTransition(prevParents, currParents);
            const sectionKey = currParents[0] ? `${currParents[0].entity_type}:${currParents[0].id}` : null;
            const sectionOpen = sectionKey ? isGroupOpen(sectionKey) : true;
            const isVisible =
									(sectionOpen) &&
									(currParents[1] ? isGroupOpen(`${currParents[1].entity_type}:${currParents[1].id}`) : true);

            return (
              <React.Fragment key={`frag-${change.version_reference_id}-${index}`}>
                {headers.map((header, localIdx) => {
                  const absoluteStart = currParents.length - headers.length;
                  const absoluteLevel = absoluteStart + localIdx;
                  const groupKey = `${header.entity_type}:${header.id}`;

                  const isCollapsible = absoluteLevel === 0 || absoluteLevel === 1;

                  // Hide block/section headers when their parent section is closed
                  if (absoluteLevel === 1 && !sectionOpen) {
                    return null;
                  }

                  const open = isGroupOpen(groupKey);

                  return (
                    <Box
                      key={`hdr-${index}-${header.entity_type}-${header.id}`}
                      sx={headerSxForLevel(absoluteLevel)}
                      onClick={() => isCollapsible && toggleGroup(groupKey)}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          ...headerTextSxForLevel(absoluteLevel),
                          cursor: isCollapsible ? 'pointer' : 'default',
                          userSelect: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {buildHeaderLabel(header, change)}
                        {isCollapsible ? (open ? <KeyboardArrowLeftIcon sx={{ fill: '#4C4E4E' }}/> :
                          <ExpandMoreIcon sx={{ fill: '#4C4E4E' }}/>) : null}
                      </Typography>
                    </Box>
                  );
                })}

                {isVisible && renderChangeItem(change, index, sourceNumbering, targetNumbering)}
              </React.Fragment>
            );
          })}


        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={handleClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: { maxWidth: 600, width: '100%', backgroundColor: 'white' },
        }}
      >
        {drawerContent}
      </Drawer>

      <VersionSettingsDialog
        open={isSettingsDialogOpen}
        onClose={() => setIsSettingsDialogOpen(false)}
        versions={versions}
        currentClaSlug={currentClaSlug}
        onVersionUpdated={handleVersionUpdated}
      />
    </>
  );
};

export const VersionComparisonPanel = React.memo(VersionComparisonPanelComponent, (prevProps, nextProps) => {
  return (
    prevProps.show === nextProps.show &&
			prevProps.currentClaSlug === nextProps.currentClaSlug &&
			prevProps.onClose === nextProps.onClose &&
			prevProps.onVersionsSelected === nextProps.onVersionsSelected &&
			prevProps.onDocumentTitleChange === nextProps.onDocumentTitleChange &&
			prevProps.onComparisonDataChange === nextProps.onComparisonDataChange
  );
});